<!-- src/components/layouts/GroupManagement.vue -->
<template>
    <div class="group-management">
        <div class="toolbar">
            <a-button type="primary" @click="showAddModal" size="large">
                <template #icon><icon-plus /></template>
                添加新组织
            </a-button>
        </div>

        <div class="org-list-container">
            <a-collapse :default-active-key="['1']" accordion class="custom-collapse">
                <draggable
                    v-model="organizations"
                    @end="onReorderOrgsEnd"
                    handle=".org-drag-handle"
                    item-key="orgId"
                >
                    <template #item="{ element: org }">
                        <a-collapse-item :key="org.orgId" :header="org.orgName" class="org-item">
                            <template #extra>
                                <div class="header-actions" @click.stop>
                                    <icon-drag-dot-vertical class="org-drag-handle" />
                                    <a-button type="text" size="small" @click="openEditModal(org)">
                                        <template #icon><icon-edit /></template>
                                    </a-button>
                                    <a-button type="text" status="danger" size="small" @click="deleteOrganizationHandler(org)">
                                        <template #icon><icon-delete /></template>
                                    </a-button>
                                </div>
                            </template>
                            
                            <!-- 分栏管理区域 -->
                            <div class="sections-area">
                                <div class="section-toolbar">
                                    <span class="area-title">分栏列表</span>
                                    <a-button type="outline" size="small" @click="showAddSectionModal(org)">
                                        <template #icon><icon-plus /></template> 添加分栏
                                    </a-button>
                                </div>
                                
                                <draggable
                                    v-model="org.sections"
                                    @end="onReorderSectionsEnd(org)"
                                    handle=".section-drag-handle"
                                    item-key="sectionId"
                                    class="section-list"
                                >
                                    <template #item="{ element: section }">
                                        <div class="section-card">
                                            <div class="section-header">
                                                <div class="section-title">
                                                    <icon-drag-dot-vertical class="section-drag-handle" />
                                                    <span>{{ section.sectionName }}</span>
                                                </div>
                                                <div class="section-actions">
                                                    <a-button type="text" size="mini" @click="showEditSectionModal(org, section)">
                                                        <icon-edit />
                                                    </a-button>
                                                    <a-button type="text" status="danger" size="mini" @click="deleteSectionHandler(org, section)">
                                                        <icon-delete />
                                                    </a-button>
                                                    <a-button type="text" size="mini" @click="showAddSiteModal(org, section)">
                                                        <icon-plus /> 站点
                                                    </a-button>
                                                </div>
                                            </div>
                                            
                                            <!-- 站点简略列表 -->
                                            <div class="site-chips">
                                                <draggable
                                                    v-model="section.sites"
                                                    @end="onReorderSitesEnd(org, section)"
                                                    item-key="siteId"
                                                    class="site-draggable-area"
                                                    group="sites"
                                                >
                                                    <template #item="{ element: site }">
                                                        <a-tag 
                                                            closable 
                                                            @close="deleteSiteHandler(org, section, site)"
                                                            @click="showEditSiteModal(org, section, site)"
                                                            class="site-tag"
                                                        >
                                                            {{ site.siteTitle }}
                                                        </a-tag>
                                                    </template>
                                                </draggable>
                                                <span v-if="!section.sites || section.sites.length === 0" class="empty-tip">暂无站点</span>
                                            </div>
                                        </div>
                                    </template>
                                </draggable>
                            </div>
                        </a-collapse-item>
                    </template>
                </draggable>
            </a-collapse>
        </div>

        <!-- 模态框组 (保持逻辑不变，样式微调) -->
        <!-- 添加组织 -->
        <a-modal v-model:visible="addModalVisible" title="添加组织" :footer="false" @close="resetNewOrganization">
            <a-form :model="newOrganization" layout="vertical">
                <a-form-item label="组织名称" required>
                    <a-input v-model="newOrganization.orgName" />
                </a-form-item>
                <a-form-item label="排序权重">
                    <a-input-number v-model="newOrganization.sortRank" />
                </a-form-item>
                <a-form-item>
                    <a-button type="primary" long @click="handleAddOrganization">确定添加</a-button>
                </a-form-item>
            </a-form>
        </a-modal>

        <!-- 编辑组织 -->
        <a-modal v-model:visible="editModalVisible" title="编辑组织" :footer="false">
            <a-form :model="editingOrganization" layout="vertical">
                <a-form-item label="组织名称" required>
                    <a-input v-model="editingOrganization.orgName" />
                </a-form-item>
                <a-form-item label="排序权重">
                    <a-input-number v-model="editingOrganization.sortRank" />
                </a-form-item>
                <a-form-item>
                    <a-button type="primary" long @click="handleUpdateOrganization">保存修改</a-button>
                </a-form-item>
            </a-form>
        </a-modal>

        <!-- 添加分栏 -->
        <a-modal v-model:visible="addSectionModalVisible" title="添加分栏" :footer="false" @close="resetNewSection">
            <a-form :model="newSection" layout="vertical">
                <a-form-item label="分栏名称" required>
                    <a-input v-model="newSection.sectionName" />
                </a-form-item>
                <a-form-item label="排序权重">
                    <a-input-number v-model="newSection.sortRank" />
                </a-form-item>
                <a-form-item>
                    <a-button type="primary" long @click="handleAddSection">确定添加</a-button>
                </a-form-item>
            </a-form>
        </a-modal>

        <!-- 编辑分栏 -->
        <a-modal v-model:visible="editSectionModalVisible" title="编辑分栏" :footer="false" @close="resetEditSection">
            <a-form :model="editingSection" layout="vertical">
                <a-form-item label="分栏名称" required>
                    <a-input v-model="editingSection.sectionName" />
                </a-form-item>
                <a-form-item label="排序权重">
                    <a-input-number v-model="editingSection.sortRank" />
                </a-form-item>
                <a-form-item>
                    <a-button type="primary" long @click="handleUpdateSection">保存修改</a-button>
                </a-form-item>
            </a-form>
        </a-modal>

        <!-- 添加/编辑 站点模态框 (复用逻辑) -->
        <a-modal v-model:visible="addSiteModalVisible" title="添加站点" :footer="false">
            <a-form :model="newSite" layout="vertical">
                <a-form-item label="标题" required>
                    <a-input v-model="newSite.siteTitle" />
                </a-form-item>
                <a-form-item label="URL" required>
                    <a-input v-model="newSite.siteUrl" />
                </a-form-item>
                <a-form-item label="图标类型">
                    <a-radio-group type="button" v-model="newSite.iconType">
                        <a-radio value="system">系统</a-radio>
                        <a-radio value="custom">自定义</a-radio>
                    </a-radio-group>
                </a-form-item>
                <a-form-item v-if="newSite.iconType === 'custom'" label="图标地址">
                    <a-input v-model="newSite.iconPath" />
                </a-form-item>
                <a-form-item>
                    <a-button type="primary" long @click="handleAddSite">添加</a-button>
                </a-form-item>
            </a-form>
        </a-modal>

        <a-modal v-model:visible="editSiteModalVisible" title="编辑站点" :footer="false">
            <a-form :model="editingSite" layout="vertical">
                <a-form-item label="标题" required>
                    <a-input v-model="editingSite.siteTitle" />
                </a-form-item>
                <a-form-item label="URL" required>
                    <a-input v-model="editingSite.siteUrl" />
                </a-form-item>
                <a-form-item label="图标类型">
                    <a-radio-group type="button" v-model="editingSite.iconType">
                        <a-radio value="system">系统</a-radio>
                        <a-radio value="custom">自定义</a-radio>
                    </a-radio-group>
                </a-form-item>
                <a-form-item v-if="editingSite.iconType === 'custom'" label="图标地址">
                    <a-input v-model="editingSite.iconPath" />
                </a-form-item>
                <a-form-item>
                    <a-button type="primary" long @click="handleUpdateSite">保存</a-button>
                </a-form-item>
            </a-form>
        </a-modal>
    </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { Message, Modal } from '@arco-design/web-vue';
import { IconEdit, IconDelete, IconPlus, IconDragDotVertical } from '@arco-design/web-vue/es/icon';
import draggable from 'vuedraggable';

export default defineComponent({
    name: "GroupManagement",
    components: {
        draggable,
        IconEdit, IconDelete, IconPlus, IconDragDotVertical
    },
    setup() {
        const store = useStore();
        
        // 模态框状态
        const addModalVisible = ref(false);
        const editModalVisible = ref(false);
        const addSectionModalVisible = ref(false);
        const editSectionModalVisible = ref(false);
        const addSiteModalVisible = ref(false);
        const editSiteModalVisible = ref(false);

        // 数据模型
        const newOrganization = reactive({ orgName: "", sortRank: 0 });
        const editingOrganization = reactive({ orgId: null, orgName: "", sortRank: 0 });
        const newSection = reactive({ sectionName: "", sortRank: 0, orgId: null });
        const editingSection = reactive({ sectionId: null, sectionName: "", sortRank: 0, orgId: null });
        const newSite = reactive({ siteTitle: "", siteUrl: "", iconType: "system", iconPath: "", sortRank: 0, sectionId: null });
        const editingSite = reactive({ siteId: null, siteTitle: "", siteUrl: "", iconType: "system", iconPath: "", sortRank: 0, sectionId: null });

        // 数据获取
        const organizations = computed({
            get: () => store.getters['organizations/allOrganizations'],
            set: (val) => { /* 可以在这里处理排序逻辑，或仅依赖 @end 事件 */ }
        });

        // Actions
        const fetchOrganizations = () => store.dispatch('organizations/fetchOrganizations');
        
        // 组织相关
        const showAddModal = () => { addModalVisible.value = true; };
        const handleAddOrganization = async () => {
            if(!newOrganization.orgName) return Message.warning('请输入组织名称');
            try {
                await store.dispatch('organizations/addOrganization', { ...newOrganization });
                Message.success('添加成功');
                await fetchOrganizations();
                addModalVisible.value = false;
                newOrganization.orgName = "";
            } catch(e) { Message.error('添加失败'); }
        };
        const resetNewOrganization = () => { newOrganization.orgName = ""; };
        
        const openEditModal = (org) => {
            Object.assign(editingOrganization, org);
            editModalVisible.value = true;
        };
        const handleUpdateOrganization = async () => {
            try {
                await store.dispatch('organizations/updateOrganization', { ...editingOrganization });
                Message.success('更新成功');
                await fetchOrganizations();
                editModalVisible.value = false;
            } catch(e) { Message.error('更新失败'); }
        };
        
        const deleteOrganizationHandler = (org) => {
            Modal.confirm({
                title: '确认删除',
                content: `确定要删除组织 "${org.orgName}" 吗？所有下属内容将被清除。`,
                onOk: async () => {
                    await store.dispatch('organizations/deleteOrganization', org.orgId);
                    await fetchOrganizations();
                    Message.success('已删除');
                }
            });
        };

        const onReorderOrgsEnd = async () => {
            const orgs = organizations.value.map((org, index) => ({ ...org, sortRank: index + 1 }));
            await store.dispatch('organizations/reorderOrganizations', orgs);
        };

        // 分栏相关
        const showAddSectionModal = (org) => {
            newSection.orgId = org.orgId;
            addSectionModalVisible.value = true;
        };
        const handleAddSection = async () => {
            if(!newSection.sectionName) return Message.warning('请输入分栏名称');
            try {
                await store.dispatch('sections/addSection', { ...newSection });
                Message.success('添加成功');
                await fetchOrganizations();
                addSectionModalVisible.value = false;
                newSection.sectionName = "";
            } catch(e) { Message.error('添加失败'); }
        };
        const resetNewSection = () => { newSection.sectionName = ""; };
        
        const showEditSectionModal = (org, section) => {
            Object.assign(editingSection, { ...section, orgId: org.orgId });
            editSectionModalVisible.value = true;
        };
        const handleUpdateSection = async () => {
            try {
                await store.dispatch('sections/updateSection', { ...editingSection });
                Message.success('更新成功');
                await fetchOrganizations();
                editSectionModalVisible.value = false;
            } catch(e) { Message.error('更新失败'); }
        };
        const resetEditSection = () => { editingSection.sectionName = ""; };
        
        const deleteSectionHandler = (org, section) => {
            Modal.confirm({
                title: '确认删除',
                content: `确定要删除分栏 "${section.sectionName}" 吗？`,
                onOk: async () => {
                    await store.dispatch('sections/deleteSection', { sectionId: section.sectionId, orgId: org.orgId });
                    await fetchOrganizations();
                    Message.success('已删除');
                }
            });
        };
        const onReorderSectionsEnd = async (org) => {
            const sections = org.sections.map((sec, index) => ({ ...sec, sortRank: index + 1 }));
            await store.dispatch('sections/reorderSections', { orgId: org.orgId, sections });
        };

        // 站点相关
        const showAddSiteModal = (org, section) => {
            newSite.sectionId = section.sectionId;
            addSiteModalVisible.value = true;
        };
        const handleAddSite = async () => {
            if(!newSite.siteTitle || !newSite.siteUrl) return Message.warning('请填写完整');
            try {
                await store.dispatch('sites/addSite', { ...newSite });
                Message.success('添加成功');
                await fetchOrganizations();
                addSiteModalVisible.value = false;
                newSite.siteTitle = ""; newSite.siteUrl = "";
            } catch(e) { Message.error('添加失败'); }
        };
        
        const showEditSiteModal = (org, section, site) => {
            Object.assign(editingSite, { ...site, sectionId: section.sectionId });
            editSiteModalVisible.value = true;
        };
        const handleUpdateSite = async () => {
            try {
                await store.dispatch('sites/updateSite', { ...editingSite });
                Message.success('更新成功');
                await fetchOrganizations();
                editSiteModalVisible.value = false;
            } catch(e) { Message.error('更新失败'); }
        };
        
        const deleteSiteHandler = (org, section, site) => {
            Modal.confirm({
                title: '删除站点',
                content: `删除 "${site.siteTitle}" ?`,
                onOk: async () => {
                    await store.dispatch('sites/deleteSite', { siteId: site.siteId, sectionId: section.sectionId });
                    await fetchOrganizations();
                    Message.success('已删除');
                }
            });
        };
        const onReorderSitesEnd = async (org, section) => {
            const sites = section.sites.map((site, index) => ({ ...site, sortRank: index + 1 }));
            await store.dispatch('sites/reorderSites', { sectionId: section.sectionId, sites });
        };

        onMounted(() => {
            fetchOrganizations();
        });

        return {
            organizations,
            addModalVisible, editModalVisible, newOrganization, editingOrganization,
            addSectionModalVisible, editSectionModalVisible, newSection, editingSection,
            addSiteModalVisible, editSiteModalVisible, newSite, editingSite,
            
            showAddModal, handleAddOrganization, resetNewOrganization,
            openEditModal, handleUpdateOrganization, deleteOrganizationHandler, onReorderOrgsEnd,
            
            showAddSectionModal, handleAddSection, resetNewSection,
            showEditSectionModal, handleUpdateSection, resetEditSection,
            deleteSectionHandler, onReorderSectionsEnd,
            
            showAddSiteModal, handleAddSite,
            showEditSiteModal, handleUpdateSite,
            deleteSiteHandler, onReorderSitesEnd
        };
    }
});
</script>

<style scoped>
.group-management {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.toolbar {
    margin-bottom: 20px;
    display: flex;
    justify-content: flex-end;
}

.org-list-container {
    flex: 1;
    overflow-y: auto;
    padding-right: 10px;
}

.custom-collapse {
    border: none;
    background: transparent;
}

.org-item {
    margin-bottom: 12px;
    border-radius: 8px;
    border: 1px solid rgba(0,0,0,0.05);
    background: rgba(255, 255, 255, 0.5);
    overflow: hidden;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.org-drag-handle {
    cursor: grab;
    color: var(--text-secondary);
}

.sections-area {
    padding: 10px;
    background: rgba(245, 245, 247, 0.5);
    border-radius: 8px;
}

.section-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.area-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-secondary);
}

.section-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.section-card {
    background: #fff;
    padding: 12px;
    border-radius: 8px;
    box-shadow: 0 2px 5px rgba(0,0,0,0.03);
    border: 1px solid rgba(0,0,0,0.03);
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(0,0,0,0.05);
}

.section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
}

.section-drag-handle {
    cursor: grab;
    color: #ccc;
}

.section-actions {
    opacity: 0.5;
    transition: opacity 0.2s;
}

.section-card:hover .section-actions {
    opacity: 1;
}

.site-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.site-draggable-area {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
    min-height: 30px;
}

.site-tag {
    cursor: pointer;
    transition: all 0.2s;
}

.site-tag:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.empty-tip {
    font-size: 12px;
    color: #ccc;
    font-style: italic;
}
</style>
